import {ReactElement, useState} from 'react'
import {flatten, groupBy, map} from 'lodash'
import {Trans} from 'react-i18next'
import {Tab, Tabs} from '@material-ui/core'
import {AccountTypeEnumIndices, CompanyEnumIndices} from '@bdswiss/common-enums'
import {Client} from '../../../../../graphql/types'
import PageSubTitle from '../../../../Common/PageSubTitle'
import messages from '../../../../../assets/messages'
import {AccountTypeItem, type AccountType} from '../AccountTypeItem'

type TabType = 'mt4' | 'mt5'

type AccountTypeStepProps = {
  viewer: Client
  allowedAccountTypes: AccountType[]
  onSelectAccountType: (accountType: string, accountSubtype: string) => void
}

const TAB_TYPES: Partial<Record<AccountTypeEnumIndices, TabType>> = {
  // FOREX
  [AccountTypeEnumIndices.ForexClassicTauroMarketsAccount]: 'mt4',
  [AccountTypeEnumIndices.ForexClassicTauroMarketsDemoAccount]: 'mt4',
  [AccountTypeEnumIndices.ForexRawTauroMarketsAccount]: 'mt4',
  [AccountTypeEnumIndices.ForexMT5SVGTauromarketsAccount]: 'mt5',
  [AccountTypeEnumIndices.ForexMT5SVGTauromarketsDemoAccount]: 'mt5',
  // PAMM
  [AccountTypeEnumIndices.PAMMTauroMarketsManagerAccount]: 'mt4',
  [AccountTypeEnumIndices.PAMMTauroMarketsMT5ManagerAccount]: 'mt5',
  // AFFILIATE: IB
  [AccountTypeEnumIndices.IntroducingBrokerTauroMarketsAccount]: 'mt4',
  // AFFILIATE: IB
  [AccountTypeEnumIndices.IntroducingBrokerTauroMarketsMauritiusAccount]: 'mt5',
  // AFFILIATE: CX
  [AccountTypeEnumIndices.AffiliateTauroMarketsAccount]: 'mt4',
}

export function AccountTypeStep(props: AccountTypeStepProps): ReactElement {
  const {viewer, allowedAccountTypes, onSelectAccountType} = props

  const {company} = viewer
  // if you want to extend companies list, please adjust TAB_TYPES constant
  const isSVG = company === CompanyEnumIndices.tauroMarkets

  const visibleTabs = Array.from(
    allowedAccountTypes.reduce<Set<TabType>>((memo, {accountType}) => {
      memo.add(TAB_TYPES[accountType])
      return memo
    }, new Set()),
  ).filter(Boolean)
    // Hide MT4 tab for affiliates 10333, 10354, 10382 and 10350's IB followers
    .filter((tab) => !([10333, 10350, 10354, 10382, 10389, 10390, 10391, 10392, 10400, 10402, 10404, 10407, 10408, 10409, 10416, 10417, 10414].includes(viewer.affiliateId ?? 0) && tab === 'mt4'))
    // Hide MT5 tab for affiliate 35954
    .filter((tab) => !(viewer.affiliateId === 35954 && tab === 'mt5'))

  // 'undefined' used as a fallback to skip filtering.
  const defaultTab: TabType | undefined = isSVG
    ? visibleTabs.length > 1
      ? 'mt4'
      : visibleTabs[0]
    : undefined
  const [selectedTab, setSelectedTab] = useState(defaultTab)
  const selectedTabIndex = visibleTabs.findIndex((tab) => tab === selectedTab) ?? false

  const handleTabChange = (_: unknown, index: number) => {
    setSelectedTab(visibleTabs[index])
  }

  const sortedAccountTypes = flatten(map(groupBy(allowedAccountTypes, 'accountSubtype')))

  const filteredAccountTypes = sortedAccountTypes.filter(({accountType}) => {
    if (selectedTab != null) return selectedTab === TAB_TYPES[accountType]
    return true
  })

  return (
    <div>
      <PageSubTitle>
        {isSVG ? (
          <Trans {...messages.chooseTradingPlatform} />
        ) : (
          <Trans {...messages.selectAccountType} />
        )}
      </PageSubTitle>
      {isSVG && visibleTabs.length > 1 ? (
        <Tabs variant="fullWidth" value={selectedTabIndex} onChange={handleTabChange}>
          {visibleTabs.map((tab) => (
            <Tab key={tab} label={getTabLabel(tab)} textColor="inherit" />
          ))}
        </Tabs>
      ) : null}
      {filteredAccountTypes.map((account: AccountType) => (
        <AccountTypeItem
          key={`${account.accountType}-${account.accountSubtype}`}
          account={account}
          hideBadge={isSVG}
          viewer={viewer}
          onSelectAccount={onSelectAccountType}
        />
      ))}
    </div>
  )
}

function getTabLabel(tab: TabType): string {
  switch (tab) {
    case 'mt4':
      return 'MT4'
    case 'mt5':
      return 'MT5'
    default:
      return '-'
  }
}
